import React, { Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Sidebar from './sidebar';
import Header from './header';
import { getPageMeta } from '../config/navigation';
import { hasSession } from '../utils/auth';
import { ScrollTrigger } from '../lib/gsap';

const COLLAPSED_KEY = 'ui.sidebarCollapsed';
// Pages that run their own entrance animation instead of the shared page fade
const OWN_ENTRANCE = new Set(['/notification']);
const DESKTOP_QUERY = '(min-width: 1024px)';

// Remembered choice, else: laptops (< 1280px) start with the slim rail to leave room for tables
const readCollapsed = () => {
    try {
        const saved = localStorage.getItem(COLLAPSED_KEY);
        if (saved !== null) return saved === '1';
    } catch {
        // storage unavailable: fall through to the default
    }
    return window.innerWidth < 1280;
};

const PageLoader = () => (
    <div className="flex min-h-[50vh] items-center justify-center" role="status" aria-label="Loading page">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent dark:border-brand-400 dark:border-t-transparent" />
    </div>
);

function AppShell() {
    const location = useLocation();
    const { label } = getPageMeta(location);
    const [collapsed, setCollapsed] = useState(readCollapsed);
    const [mobileOpen, setMobileOpen] = useState(false);
    const mainRef = useRef(null);

    // Each new page starts at the top, closes the mobile drawer and names the browser tab
    // Block bodies matter: newer browsers return a Promise from scrollTo(), and an effect
    // may only return a cleanup function
    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname, location.search]);
    useEffect(() => {
        document.title = label;
    }, [label]);

    // While the mobile drawer is open: lock page scroll, close on Escape or on resize to desktop
    useEffect(() => {
        if (!mobileOpen) return undefined;
        const desktop = window.matchMedia(DESKTOP_QUERY);
        const close = () => setMobileOpen(false);
        const onKey = (e) => e.key === 'Escape' && close();
        const onResize = (e) => e.matches && close();

        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKey);
        desktop.addEventListener('change', onResize);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', onKey);
            desktop.removeEventListener('change', onResize);
        };
    }, [mobileOpen]);

    // Page height changes when data arrives; re-measure scroll-triggered animations (debounced)
    useEffect(() => {
        const el = mainRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        let timer;
        const observer = new ResizeObserver(() => {
            clearTimeout(timer);
            timer = setTimeout(() => ScrollTrigger.refresh(), 200);
        });
        observer.observe(el);
        return () => {
            clearTimeout(timer);
            observer.disconnect();
        };
    }, []);

    const toggleCollapsed = useCallback(() => {
        setCollapsed((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
            } catch {
                // preference just won't persist
            }
            return next;
        });
    }, []);

    const openMobileNav = useCallback(() => setMobileOpen(true), []);
    const closeMobileNav = useCallback(() => setMobileOpen(false), []);

    return (
        <div className="min-h-screen">
            <Sidebar
                collapsed={collapsed}
                onToggleCollapsed={toggleCollapsed}
                mobileOpen={mobileOpen}
                onCloseMobile={closeMobileNav}
            />

            {/* Content snaps to its new offset while only the sidebar animates: animating the
                padding would re-layout every table and chart on each frame (laggy on slow PCs) */}
            <div className={collapsed ? 'lg:pl-[72px]' : 'lg:pl-64'}>
                <Header onOpenMobileNav={openMobileNav} />
                <main ref={mainRef} className="px-4 py-6 sm:px-6 lg:px-8">
                    <Suspense fallback={<PageLoader />}>
                        {/* Keyed by URL so each page remounts and replays the entrance. It's a CSS
                            animation, so it runs on the compositor and stays smooth while the page
                            renders its data (see tailwind.config.js `page-in`) */}
                        <div
                            key={location.pathname + location.search}
                            className={OWN_ENTRANCE.has(location.pathname) ? undefined : 'animate-page-in'}
                        >
                            <Outlet />
                        </div>
                    </Suspense>
                </main>
            </div>
        </div>
    );
}

// Signed-out visitors go to the login page before any of the shell renders
const AppLayout = () => (hasSession() ? <AppShell /> : <Navigate to="/login" replace />);

export default AppLayout;
